import { useState } from 'react';
import type {
  CreatedToken,
  OauthConnection,
  TokenScope,
  TokenSummary,
  TrackerConnection,
} from '@forecastify/shared';
import { ACCESS_TOKENS_ENABLED, TOKEN_SCOPES } from '@forecastify/shared';
import { Button, Dialog, Icon, Switch, TextInput, Tooltip } from '@forecastify/ui';
import { useT } from '../../i18n';
import { Card, CardColumn, Field } from '../card';
import { ProviderLogo } from '../integrations/provider-logo';
import { GoogleMark } from '../auth/google-mark';
/**
 * ⚠️ **The two calendar marks are kept on disk and no longer imported here** —
 * `assets/google-calendar.svg` and `assets/outlook.svg`, beside `Jira.svg` and
 * `YouTrack_Icon.svg`. The card that used them is removed below; when it returns, so do they, and
 * the note about sizing the Google mark by HEIGHT (it is 800x859, not square) is the thing worth
 * keeping until then.
 */

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Integrations — what this account is connected to, and what it can open.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ TWO CARDS CAME DOWN ON 14 SEP. ONE CAME BACK, AND THE NOTE THAT      │
 * │ TOOK IT DOWN IS WHY.                                                      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Both were removed for one reason, stated once: *"thirteen mockup keys' worth of surface for a
 * capability nothing in the product consumes yet"*, and *"it comes back when there is something
 * behind it"*.
 *
 * ⚠️⚠️ **C51 IS THE SOMETHING BEHIND IT, SO THE TOKEN CARD IS BACK AND THE CALENDAR CARD IS
 * NOT.** A personal access token is the credential an MCP client authenticates with over stdio
 * (C51.1, R-39), and this card is the **only** place in the product one can be minted. Without it,
 * connecting a chat client would mean being handed a row by somebody with a database shell, which
 * is not a product. The calendar switches still have nothing behind them and stay down.
 *
 * ⚠️ **It came back LARGER than it went away.** C51.1 added the scope switches, because a token
 * that opens every door is not what somebody connecting a chat client wants, and R-39 refuses to
 * default them.
 *
 * ⚠️ **Personal access tokens are THIRTEEN undrawn mockup keys** — `usTokens`, `usTokenCreate`,
 * `usTokenReveal*`, `usTokenCopy` and the rest are logic with no UI anywhere
 * (`docs/plan/mockup-findings.md`). `chunks.md` says build it, so this is ours.
 *
 * ⚠️ **The value is shown ONCE, in a dialog, and cannot be recovered.** The database holds a hash
 * and the last four characters — there is no endpoint that returns it again, which is the whole
 * point of hashing it. A list you could re-read would make the hash decoration.
 */

export interface TokenHandlers {
  /**
   * ⚠️ **C51.1 · a name is no longer enough.** R-39: a token carries a scope set, and the server
   * refuses to guess one — *"defaulting the scopes to everything the holder can do would make the
   * safe choice the one somebody has to remember to make"*. The workspace is added by the route,
   * which is the layer that knows which one is active.
   */
  readonly onCreate?: (request: {
    readonly name: string;
    readonly scopes: readonly TokenScope[];
  }) => Promise<CreatedToken>;
  readonly onRevoke?: (id: string) => Promise<void>;
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ C52 · THE OTHER ANSWER TO *"WHAT CAN REACH MY WORKSPACES WITHOUT ME"* │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A key I minted and an application I approved are the same question with two answers, and somebody
 * looking for one is looking for both. A separate screen would be a second place to remember to
 * check — and the failure mode of forgetting is a live credential nobody revoked.
 *
 * ⚠️ **Disconnecting revokes the tokens with it, in the database** (`0124`'s trigger), so this
 * button is not a bookkeeping gesture: the application stops working on its next request.
 */
/**
 * 22 Oct 2026 · the Google account connected to this one — signing in with Google connects it, and
 * this card connects and disconnects it by hand. It manages nothing but signing in for now.
 */
export interface GoogleConnection {
  /** Whether Google sign-in is switched on at all — the card is absent when it is not. */
  readonly configured: boolean;
  readonly connected: { readonly email: string | null } | null;
}

export interface GoogleHandlers {
  readonly onConnect: () => void;
  readonly onDisconnect: () => void;
}

export interface ApplicationHandlers {
  readonly onDisconnect?: (clientId: string, workspaceId: string) => Promise<void>;
}

/**
 * ⚠️ **Catalogue keys as a literal map, not `t(\`account.tokens.scope.${scope}\`)`.** The
 * translator is typed on the key union, so a computed key is untyped — and rule 7's whole point is
 * that a missing string fails the build rather than rendering a key at somebody.
 */
const SCOPE_LABEL = {
  'forecastify:read': 'account.tokens.scope.read',
  'forecastify:write': 'account.tokens.scope.write',
  'forecastify:contracts': 'account.tokens.scope.contracts',
} as const;

export function IntegrationsSection({
  tokens,
  handlers,
  connections,
  applications,
  applicationHandlers,
  busy = false,
  onConnect,
  onDisconnect,
  google = null,
  googleHandlers,
  googleBusy = false,
}: {
  /** 22 Oct 2026 · null while it loads; the card is drawn once it has landed. */
  readonly google?: GoogleConnection | null;
  readonly googleHandlers?: GoogleHandlers;
  readonly googleBusy?: boolean;
  readonly tokens: readonly TokenSummary[];
  readonly handlers?: TokenHandlers;
  /** ⚠️ Null until it lands — *"you have connected nothing"* and *"not read yet"* differ. */
  readonly connections: readonly TrackerConnection[] | null;
  /** ⚠️ C52 · the same distinction, for the same reason. Null is *"not read yet"*. */
  readonly applications: readonly OauthConnection[] | null;
  readonly applicationHandlers?: ApplicationHandlers;
  readonly busy?: boolean;
  readonly onConnect: (instanceId: string) => void;
  readonly onDisconnect: (remoteSiteId: string) => void;
}) {
  const t = useT();

  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  /**
   * ⚠️ **Read is on and the other two are off, which is the least-privilege default rather than a
   * convenience.** A token that can only read is the one somebody almost always wants, and making
   * the narrow option the default means the wide one is a decision somebody took on purpose.
   */
  const [scopes, setScopes] = useState<readonly TokenScope[]>(['forecastify:read']);
  const [revealed, setRevealed] = useState<CreatedToken | null>(null);
  const [copied, setCopied] = useState(false);

  const live = tokens.filter((token) => token.revokedAt === null);

  return (
    <CardColumn>
      {/* 22 Oct 2026 · the Google account, first: it is how this person signs in. Absent while
          Google sign-in is off — unless one is connected, which must stay disconnectable. */}
      {google !== null && (google.configured || google.connected !== null) ? (
        <Card title={t('account.google.title')}>
          <div className="flex items-center gap-3">
            <span className="grid size-4 shrink-0 place-items-center">
              <GoogleMark />
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12.5px]">
                {google.connected === null
                  ? t('account.google.notConnected')
                  : google.connected.email === null
                    ? t('account.google.connected')
                    : t('account.google.connectedAs', { email: google.connected.email })}
              </div>
              <div className="text-muted-foreground text-[11.5px]">{t('account.google.lede')}</div>
            </div>
            {googleHandlers === undefined ? null : google.connected !== null ? (
              <Button
                variant="outline"
                size="sm"
                disabled={googleBusy}
                onClick={googleHandlers.onDisconnect}
              >
                {t('account.google.disconnect')}
              </Button>
            ) : google.configured ? (
              <Button size="sm" disabled={googleBusy} onClick={googleHandlers.onConnect}>
                <Icon name="link" size={13} />
                {t('account.google.connect')}
              </Button>
            ) : null}
          </div>
        </Card>
      ) : null}
      {/**
       * ⚠️⚠️ **HERE, NOT IN SETTINGS → INTEGRATIONS, AND THE REASON IS WHO PRESSES IT.** That screen
       * needs `integration.manage`, which most of a workspace does not have — and this is the one
       * action in the whole integration that belongs to the person rather than the administrator.
       * A Connect button only an admin can reach is a Connect button nobody presses.
       *
       * ⚠️ Absent entirely when the workspace has no tracker, rather than an empty card explaining
       * a feature nobody here uses.
       */}
      {connections !== null && connections.length > 0 ? (
        <Card title={t('account.connections.title')}>
          <div className="flex flex-col gap-3">
            {connections.map((row) => (
              <div key={row.instanceId} className="flex items-center gap-3">
                {/* ⚠️ Item 18 · the tracker's own mark, which this list drew and this one did
                    not. A row that says only *"Forecastify v3"* does not tell somebody whether
                    they are about to connect their Jira account or their YouTrack one. */}
                <ProviderLogo provider={row.extensionId} size={16} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12.5px]">{row.name}</div>
                  <div className="text-muted-foreground flex min-w-0 items-center gap-1.5 text-[11.5px]">
                    <span className="truncate">
                      {row.remoteSiteId === null
                        ? /* ⚠️ Nothing to key a credential to yet — say which action fixes it. */
                          t('account.connections.notDiscovered')
                        : row.connected
                          ? t('account.connections.connected')
                          : t('account.connections.notConnected')}
                    </span>
                    {/* 21 Oct 2026 · the chrome's warning, and here it says why — for the same
                        people only: somebody who does not log time is not warned (26 Oct). */}
                    {row.required && row.remoteSiteId !== null && !row.connected ? (
                      <Tooltip asChild content={t('account.connections.warning')}>
                        <span
                          role="img"
                          tabIndex={0}
                          aria-label={t('account.connections.warning')}
                          className="text-status-warn-text focus-visible:ring-ring/50 inline-flex shrink-0 rounded-sm outline-none focus-visible:ring-[3px]"
                        >
                          <Icon name="triangle-alert" size={13} />
                        </span>
                      </Tooltip>
                    ) : null}
                  </div>
                </div>
                {row.connected ? (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy || row.remoteSiteId === null}
                    onClick={() => {
                      onDisconnect(row.remoteSiteId ?? '');
                    }}
                  >
                    {t('account.connections.disconnect')}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    disabled={busy || row.remoteSiteId === null}
                    onClick={() => {
                      onConnect(row.instanceId);
                    }}
                  >
                    <Icon name="link" size={13} />
                    {t('account.connections.connect')}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {/**
       * ⚠️⚠️ **THE CALENDAR SWITCHES ARE REMOVED, AND STAY REMOVED.**
       *
       * Asked for on 14 Sep: two disabled switches for an integration that does not exist. Unlike
       * the token card below, nothing has since arrived to put behind them.
       *
       * ⚠️ **It comes back when there is something behind it**, with its two marks
       * (`assets/google-calendar.svg`, `assets/outlook.svg`) and the `account.integrations.*`
       * keys, all of which are kept for that.
       */}

      {/* ⚠️ 27 Oct 2026 · OFF UNTIL THE TOKENS DO SOMETHING — `ACCESS_TOKENS_ENABLED` says why. */}
      {ACCESS_TOKENS_ENABLED ? (
        <Card
          title={t('account.tokens.title')}
          actions={
            naming ? undefined : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setNaming(true);
                }}
              >
                {t('account.tokens.create')}
              </Button>
            )
          }
        >
          {naming && (
            <div className="mb-3 flex flex-col gap-2 border-b pb-3">
              <Field label={t('account.tokens.nameLabel')}>
                <TextInput
                  placeholder={t('account.tokens.namePlaceholder')}
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                  }}
                />
              </Field>
              {/**
               * ⚠️⚠️ **THE SCOPES ARE PART OF MINTING, NOT A SETTING AFTERWARDS.** R-39: a scope set
               * narrows what the credential may do and can never widen it, so it is chosen once, in
               * front of the person who is about to copy a key into a config file. There is no edit
               * path on purpose — changing what a live token can do is a new token.
               */}
              <Field label={t('account.tokens.scopesLabel')}>
                <div className="flex flex-col gap-1.5">
                  {TOKEN_SCOPES.map((scope) => (
                    <Switch
                      key={scope}
                      label={t(SCOPE_LABEL[scope])}
                      checked={scopes.includes(scope)}
                      onCheckedChange={(next) => {
                        setScopes((current) =>
                          next ? [...current, scope] : current.filter((held) => held !== scope),
                        );
                      }}
                    />
                  ))}
                </div>
              </Field>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  /* ⚠️ A token with no scopes authenticates and then denies everything — a support
                   * conversation about a 403 whose cause is invisible on both screens. `0120` refuses
                   * it at the database too; this is the half somebody can see. */
                  disabled={name.trim() === '' || scopes.length === 0}
                  onClick={() => {
                    if (handlers?.onCreate === undefined) return;
                    void handlers.onCreate({ name: name.trim(), scopes }).then((created) => {
                      setRevealed(created);
                      setNaming(false);
                      setName('');
                      setScopes(['forecastify:read']);
                      setCopied(false);
                    });
                  }}
                >
                  {t('account.tokens.create')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setNaming(false);
                    setName('');
                    setScopes(['forecastify:read']);
                  }}
                >
                  {t('account.email.cancel')}
                </Button>
              </div>
            </div>
          )}

          {live.length === 0 && !naming ? (
            <p className="text-muted-foreground text-[12px]">{t('account.tokens.empty')}</p>
          ) : (
            <ul className="flex flex-col">
              {tokens.map((token) => (
                <li
                  key={token.id}
                  className="flex items-center gap-2.5 border-b py-2 last:border-0"
                >
                  <Icon name="key" size={14} className="text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12.5px]">
                      {token.name}
                      {token.revokedAt !== null && (
                        <span className="text-muted-foreground">
                          {' '}
                          · {t('account.tokens.revoked')}
                        </span>
                      )}
                    </p>
                    {/* ⚠️ The last four characters, mono. Enough to tell two tokens apart, and not
                      enough to be a credential — which is exactly why the column stores only
                      those. */}
                    <p className="text-muted-foreground text-[11px]">
                      ••••{token.lastFour} ·{' '}
                      {t('account.tokens.lastUsed', {
                        when: token.lastUsedAt ?? t('account.tokens.never'),
                      })}
                    </p>
                    {/* ⚠️ C51.1 · what this key actually opens. A list of identical-looking tokens
                      with different authority is a list somebody cannot safely revoke from — and
                      revoking is the only action this row offers. */}
                    {token.scopes.length > 0 && (
                      <p className="text-muted-foreground text-[11px]">
                        {token.scopes.map((scope) => t(SCOPE_LABEL[scope])).join(' · ')}
                      </p>
                    )}
                  </div>
                  {token.revokedAt === null && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        void handlers?.onRevoke?.(token.id);
                      }}
                    >
                      {t('account.tokens.revoke')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : null}

      {/* ── C52 · the applications a person approved ──────────────────────────────────────── */}
      {/**
       * ┌───────────────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ IMMEDIATELY BELOW THE KEYS, BECAUSE IT IS THE SAME QUESTION.         │
       * └───────────────────────────────────────────────────────────────────────────┘
       *
       * ⚠️ **Hidden entirely when there is nothing**, unlike the token list's empty sentence. A
       * person who has never connected an application has no concept to be told about — a card
       * explaining OAuth to somebody who has not used it is noise on a settings screen. The card
       * appears the moment there is something to revoke, which is the moment it means anything.
       */}
      {applications !== null && applications.length > 0 && (
        <Card title={t('account.apps.title')}>
          <p className="text-muted-foreground mb-3 text-[12px]">{t('account.apps.lede')}</p>
          <ul className="flex flex-col">
            {applications.map((app) => (
              <li
                key={`${app.clientId}:${app.workspaceId}`}
                className="flex items-center gap-2.5 border-b py-2 last:border-0"
              >
                <Icon name="plug" size={14} className="text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  {/* ⚠️ The name is the application's OWN words — its metadata document says it —
                      which is why the workspace and the scopes are beside it rather than trusted
                      to it. `consent-screen.tsx` makes the same split at the moment of approval. */}
                  <p className="truncate text-[12.5px]">{app.clientName}</p>
                  <p className="text-muted-foreground text-[11px]">
                    {app.workspaceName === '' ? app.clientId : app.workspaceName} ·{' '}
                    {t('account.tokens.lastUsed', {
                      when: app.lastUsedAt ?? t('account.tokens.never'),
                    })}
                  </p>
                  <p className="text-muted-foreground text-[11px]">
                    {app.scopes.map((scope) => t(SCOPE_LABEL[scope])).join(' · ')}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void applicationHandlers?.onDisconnect?.(app.clientId, app.workspaceId);
                  }}
                >
                  {t('account.apps.disconnect')}
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* ── the reveal-once dialog ────────────────────────────────────────────────────────── */}
      <Dialog
        open={revealed !== null}
        onOpenChange={(open) => {
          if (!open) setRevealed(null);
        }}
        title={t('account.tokens.revealTitle')}
        description={t('account.tokens.revealHint')}
        /* ⚠️ Required by `Dialog`, and rightly: the close control is icon-only, so without a name it
         * is a button a screen reader announces as "button". */
        closeLabel={t('account.email.cancel')}
      >
        {revealed !== null && (
          <div className="flex flex-col gap-2">
            <code className="bg-muted rounded px-2 py-1.5 text-[12px] break-all">
              {revealed.value}
            </code>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => {
                  /* ⚠️ `writeText` can reject — a denied permission, an insecure context, a browser
                   * that has no clipboard API. The token is on screen and selectable either way, so
                   * a failed copy must not look like a failed token. */
                  /* ⚠️ Widened, not optional-chained: `navigator.clipboard` is typed as always present
                   * and is absent in jsdom and on insecure origins. */
                  const clipboard = (
                    navigator as unknown as {
                      clipboard?: { writeText?: (text: string) => Promise<void> };
                    }
                  ).clipboard;
                  void clipboard
                    ?.writeText?.(revealed.value)
                    .then(() => {
                      setCopied(true);
                    })
                    .catch(() => {
                      setCopied(false);
                    });
                }}
              >
                {copied ? t('account.tokens.copied') : t('account.tokens.copy')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setRevealed(null);
                }}
              >
                {t('account.tokens.done')}
              </Button>
            </div>
          </div>
        )}
      </Dialog>
    </CardColumn>
  );
}
